/**
 * Documents Workspace — Enterprise DMS home.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │  8 dashboard cards (Total / Active / Expiring / Expired / …)      │
 *   ├──────────────────┬──────────────────────────────────────────────┤
 *   │  Category tree   │  Filtered document list (right pane)          │
 *   │  (5 roots)       │  · search · status filter · upload            │
 *   │                  │  · per-row: metadata, expiration pill, actions │
 *   └──────────────────┴──────────────────────────────────────────────┘
 *
 * Data source: /api/documents-v2/*  (unified DMS collection). Phase-1 metadata
 * is manual; Phase-2 will fill it via Claude 4.6 Vision OCR.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, RefreshCw, Files, ShieldCheck, Clock3, CalendarX2, Archive,
  History, ClipboardCheck, Sparkles, Search, Upload, FolderTree, X,
  Trash2, Download, Edit3, ChevronRight,
} from "lucide-react";
import { format, parseISO, differenceInCalendarDays } from "date-fns";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import {
  v2ListDocuments, v2Dashboard, v2Categories, v2Migrate,
  v2ArchiveDocument, v2DeleteDocument, v2Upload,
  v2FetchDownloadBlob,
  V2_ROOT_LABEL,
} from "@/lib/api";

const STATUS_META = {
  active:         { label: "Active",         bg: "rgba(15,109,93,0.10)",  fg: "#0F6D5D" },
  pending_review: { label: "Awaiting Review", bg: "rgba(180,83,9,0.10)",   fg: "#B45309" },
  archived:       { label: "Archived",       bg: "#F1F5F9",               fg: "#475569" },
  expired:        { label: "Expired",        bg: "rgba(155,28,28,0.10)",  fg: "#9B1C1C" },
};

const FILTERS = [
  { id: "all",             label: "All",              q: {} },
  { id: "active",          label: "Active",           q: { status: "active" } },
  { id: "expiring",        label: "Expiring 30d",     q: { expiring_within_days: 30 } },
  { id: "expired",         label: "Expired",          q: { expired: true } },
  { id: "pending_review",  label: "Awaiting Review",  q: { status: "pending_review" } },
  { id: "archived",        label: "Archived",         q: { include_archived: true, status: "archived" } },
];

export default function DocumentsWorkspace({ onSessionExpired }) {
  const [dashboard, setDashboard] = useState(null);
  const [tree, setTree] = useState({});
  const [entityTypes, setEntityTypes] = useState([]);
  const [rootKey, setRootKey] = useState("all");
  const [catKey, setCatKey] = useState(null);
  const [subKey, setSubKey] = useState(null);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [migrating, setMigrating] = useState(false);

  const boot = useCallback(async () => {
    try {
      const [cats, dash] = await Promise.all([v2Categories(), v2Dashboard()]);
      setTree(cats.tree || {});
      setEntityTypes(cats.entity_types || []);
      setDashboard(dash);
    } catch (err) {
      if (err?.response?.status === 401) { onSessionExpired?.(); return; }
      toast.error("Could not load Documents workspace.");
    }
  }, [onSessionExpired]);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const filt = FILTERS.find((f) => f.id === filter) || FILTERS[0];
      const params = { limit: 300, ...(filt.q || {}) };
      // "expired": expiring_within_days doesn't map — use expiration filter server-side helper
      if (filt.id === "expired") {
        delete params.expired;
        params.status = "expired";
      }
      if (rootKey !== "all") params.category_root = rootKey;
      if (catKey) params.category = catKey;
      if (subKey) params.subcategory = subKey;
      if (query.trim()) params.q = query.trim();
      const res = await v2ListDocuments(params);
      setDocs(res.documents || []);
    } catch { toast.error("Failed to load documents."); }
    finally { setLoading(false); }
  }, [rootKey, catKey, subKey, filter, query]);

  useEffect(() => { boot(); }, [boot]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const migrate = async () => {
    setMigrating(true);
    try {
      const res = await v2Migrate();
      toast.success(res.imported
        ? `Migrated ${res.imported} document${res.imported === 1 ? "" : "s"}. ${res.linked_to_employees} linked to employees.`
        : `Everything up to date. ${res.skipped_already_present} already migrated.`);
      await boot(); await loadDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed.");
    } finally { setMigrating(false); }
  };

  const onArchive = async (d) => {
    if (!window.confirm(`Archive "${d.display_name}"?`)) return;
    try { await v2ArchiveDocument(d.id); toast.success("Archived."); loadDocs(); boot(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };
  const onDelete = async (d) => {
    if (!window.confirm(`Delete "${d.display_name}"? This can be restored from Archived.`)) return;
    try { await v2DeleteDocument(d.id); toast.success("Deleted."); loadDocs(); boot(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };
  const onDownload = async (d) => {
    try {
      const blob = await v2FetchDownloadBlob(d.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = d.filename || "document";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed.");
    }
  };

  return (
    <div className="space-y-6" data-testid="documents-workspace">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-medium mb-1" style={{ color: "#7A5E12" }}>Enterprise DMS</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "#0B2545" }}>
            Documents
          </h1>
          <p className="mt-1 text-sm" style={{ color: "#64748B" }}>
            The single source of truth for every record — auto-linked to leads, facilities, clients, and employees.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={migrate} disabled={migrating}
                  className="h-10" style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                  data-testid="documents-migrate">
            {migrating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Sync Legacy
          </Button>
          <Button onClick={() => setShowUpload(true)} className="h-10"
                  style={{ background: "#0B2545", color: "#FFD774" }}
                  data-testid="documents-upload-open">
            <Upload className="h-4 w-4 mr-2" /> Upload
          </Button>
        </div>
      </div>

      <DashboardCards data={dashboard} />

      <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-4">
        {/* LEFT — category tree */}
        <aside className="rounded-2xl border bg-white p-3 h-fit" style={{ borderColor: "#E2E8F0" }}
               data-testid="documents-categories">
          <div className="text-[10px] uppercase tracking-[0.18em] font-medium px-2 mb-2 flex items-center gap-1.5"
               style={{ color: "#7A5E12" }}>
            <FolderTree className="h-3 w-3" /> Categories
          </div>
          <button
            type="button"
            onClick={() => { setRootKey("all"); setCatKey(null); setSubKey(null); }}
            className="w-full text-left px-2.5 py-1.5 rounded text-sm font-medium"
            style={{
              background: rootKey === "all" ? "rgba(212,175,55,0.16)" : "transparent",
              color: rootKey === "all" ? "#7A5E12" : "#0B2545",
            }}
            data-testid="doc-cat-all"
          >
            All Documents
          </button>
          {Object.entries(V2_ROOT_LABEL).map(([root, label]) => {
            const isRootActive = rootKey === root;
            const cats = tree[root] || {};
            return (
              <div key={root} className="mt-1">
                <button
                  type="button"
                  onClick={() => { setRootKey(root); setCatKey(null); setSubKey(null); }}
                  className="w-full text-left px-2.5 py-1.5 rounded text-sm font-medium flex items-center justify-between"
                  style={{
                    background: isRootActive ? "rgba(11,37,69,0.06)" : "transparent",
                    color: isRootActive ? "#0B2545" : "#475569",
                  }}
                  data-testid={`doc-cat-root-${root}`}
                >
                  <span>{label}</span>
                  <ChevronRight
                    className="h-3.5 w-3.5 transition-transform"
                    style={{ transform: isRootActive ? "rotate(90deg)" : "none" }}
                  />
                </button>
                {isRootActive && (
                  <ul className="ml-2 mt-1 space-y-0.5 border-l" style={{ borderColor: "#E2E8F0" }}>
                    {Object.keys(cats).map((cat) => {
                      const isActive = catKey === cat;
                      return (
                        <li key={cat}>
                          <button
                            type="button"
                            onClick={() => { setCatKey(cat); setSubKey(null); }}
                            className="w-full text-left pl-3 pr-2 py-1 rounded text-xs"
                            style={{
                              background: isActive ? "rgba(212,175,55,0.12)" : "transparent",
                              color: isActive ? "#7A5E12" : "#64748B",
                              fontWeight: isActive ? 600 : 400,
                            }}
                            data-testid={`doc-cat-${root}-${cat}`}
                          >
                            {_prettify(cat)}
                          </button>
                          {isActive && (
                            <ul className="ml-2 mt-0.5 space-y-0.5">
                              {(cats[cat] || []).map((sub) => (
                                <li key={sub.key}>
                                  <button
                                    type="button"
                                    onClick={() => setSubKey(subKey === sub.key ? null : sub.key)}
                                    className="w-full text-left pl-3 pr-2 py-0.5 text-[11px]"
                                    style={{
                                      color: subKey === sub.key ? "#0B2545" : "#94A3B8",
                                      fontWeight: subKey === sub.key ? 600 : 400,
                                    }}
                                    data-testid={`doc-sub-${sub.key}`}
                                  >
                                    · {sub.label}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </aside>

        {/* RIGHT — search + filter + list */}
        <section className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#94A3B8" }} />
              <Input value={query} onChange={(e) => setQuery(e.target.value)}
                     placeholder="Search name, tag, license #, entity…"
                     className="pl-9 h-10" data-testid="documents-search" />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {FILTERS.map((f) => (
                <button key={f.id} onClick={() => setFilter(f.id)}
                        data-testid={`docs-filter-${f.id}`}
                        className="text-xs px-3 py-1.5 rounded-full border transition-colors"
                        style={{
                          background: filter === f.id ? "#0B2545" : "#FFFFFF",
                          color: filter === f.id ? "#FFD774" : "#475569",
                          borderColor: filter === f.id ? "#0B2545" : "#E2E8F0",
                        }}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="grid place-items-center py-20">
              <Loader2 className="h-6 w-6 animate-spin" style={{ color: "#0B2545" }} />
            </div>
          ) : docs.length === 0 ? (
            <EmptyState onUpload={() => setShowUpload(true)} />
          ) : (
            <ul className="space-y-2" data-testid="documents-list">
              {docs.map((d) => (
                <DocRow key={d.id} doc={d}
                        onDownload={() => onDownload(d)}
                        onArchive={() => onArchive(d)}
                        onDelete={() => onDelete(d)} />
              ))}
            </ul>
          )}
        </section>
      </div>

      <UploadDialog
        open={showUpload}
        onClose={() => setShowUpload(false)}
        entityTypes={entityTypes}
        tree={tree}
        rootKey={rootKey !== "all" ? rootKey : "business"}
        onUploaded={() => { setShowUpload(false); loadDocs(); boot(); }}
      />
    </div>
  );
}

// ---------- dashboard cards ---------------------------------------------
const DashboardCards = ({ data }) => {
  const cards = [
    { label: "Total Documents",     value: data?.total_documents ?? "—",     icon: Files,           accent: "#0B2545" },
    { label: "Active",              value: data?.active_documents ?? "—",    icon: ShieldCheck,     accent: "#0F6D5D" },
    { label: "Expiring 30d",        value: data?.expiring_soon ?? "—",       icon: Clock3,          accent: "#B45309" },
    { label: "Expired",             value: data?.expired ?? "—",             icon: CalendarX2,      accent: "#9B1C1C" },
    { label: "Archived",            value: data?.archived ?? "—",            icon: Archive,         accent: "#475569" },
    { label: "Recently Updated",    value: data?.recently_updated ?? "—",    icon: History,         accent: "#1D4ED8" },
    { label: "Awaiting Review",     value: data?.awaiting_review ?? "—",     icon: ClipboardCheck,  accent: "#7A5E12" },
    { label: "AI Processing Queue", value: data?.ai_processing_queue ?? "—", icon: Sparkles,        accent: "#7C3AED" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="documents-dashboard">
      {cards.map((c) => (
        <div key={c.label} className="rounded-2xl border bg-white p-4"
             style={{ borderColor: "#E2E8F0" }}
             data-testid={`docs-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="flex items-center justify-between">
            <span className="grid place-items-center h-9 w-9 rounded-lg"
                  style={{ background: `${c.accent}14`, color: c.accent }}>
              <c.icon className="h-4 w-4" />
            </span>
            <span className="h-1.5 w-8 rounded-full" style={{ background: c.accent }} />
          </div>
          <div className="mt-3 font-display text-2xl font-bold" style={{ color: "#0B2545" }}>{c.value}</div>
          <div className="text-[11px] uppercase tracking-[0.16em] mt-1" style={{ color: "#64748B" }}>{c.label}</div>
        </div>
      ))}
    </div>
  );
};

// ---------- doc row ------------------------------------------------------
const DocRow = ({ doc, onDownload, onArchive, onDelete }) => {
  const status = STATUS_META[doc.status] || STATUS_META.active;
  const expPill = _expirationPill(doc.expiration_date);
  return (
    <li className="rounded-xl border bg-white p-3 flex items-center gap-3"
        style={{ borderColor: "#E2E8F0" }}
        data-testid={`doc-row-${doc.id}`}>
      <span className="grid place-items-center h-10 w-10 rounded-lg shrink-0"
            style={{ background: "rgba(11,37,69,0.06)", color: "#0B2545" }}>
        <Files className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-medium truncate" style={{ color: "#0B2545" }}>{doc.display_name}</div>
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                style={{ background: status.bg, color: status.fg }}>{status.label}</span>
          {expPill && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={{ background: expPill.bg, color: expPill.fg }}>{expPill.label}</span>
          )}
          {doc.ai_status === "pending" && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={{ background: "rgba(124,58,237,0.10)", color: "#7C3AED" }}>AI Pending</span>
          )}
        </div>
        <div className="text-xs mt-0.5 truncate" style={{ color: "#64748B" }}>
          {_categoryLabel(doc)} · {doc.entity_type}
          {doc.entity_label ? ` → ${doc.entity_label}` : ""}
          {doc.filename ? ` · ${doc.filename}` : ""} · {_fmtBytes(doc.size_bytes)}
        </div>
        <div className="text-[11px] mt-0.5" style={{ color: "#94A3B8" }}>
          Uploaded {_fmtDT(doc.uploaded_at)} by {doc.uploaded_by || "—"}
          {doc.expiration_date ? ` · Expires ${_fmtDate(doc.expiration_date)}` : ""}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button type="button" onClick={onDownload}
                className="h-8 w-8 grid place-items-center rounded hover:bg-slate-50"
                title="Download"
                style={{ color: "#0B2545" }}
                data-testid={`doc-download-${doc.id}`}>
          <Download className="h-3.5 w-3.5" />
        </button>
        {!doc.is_archived && (
          <button type="button" onClick={onArchive}
                  className="h-8 w-8 grid place-items-center rounded hover:bg-slate-50"
                  title="Archive"
                  style={{ color: "#475569" }}
                  data-testid={`doc-archive-${doc.id}`}>
            <Archive className="h-3.5 w-3.5" />
          </button>
        )}
        <button type="button" onClick={onDelete}
                className="h-8 w-8 grid place-items-center rounded hover:bg-slate-50"
                title="Delete"
                style={{ color: "#9B1C1C" }}
                data-testid={`doc-delete-${doc.id}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </li>
  );
};

// ---------- upload dialog -----------------------------------------------
const UploadDialog = ({ open, onClose, tree, entityTypes, rootKey, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [entityType, setEntityType] = useState("business");
  const [entityId, setEntityId] = useState("");
  const [entityLabel, setEntityLabel] = useState("");
  const [catRoot, setCatRoot] = useState(rootKey);
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [documentTitle, setDocumentTitle] = useState("");
  const [expiration, setExpiration] = useState("");
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setFile(null); setDisplayName(""); setDocumentTitle("");
      setExpiration(""); setNotes(""); setTags(""); setProgress(0);
      setCatRoot(rootKey); setCategory(""); setSubcategory("");
      setEntityId(""); setEntityLabel("");
      setEntityType(_entityForRoot(rootKey));
    }
  }, [open, rootKey]);

  const cats = tree[catRoot] || {};
  const subs = cats[category] || [];

  const pick = (f) => {
    if (!f) return;
    setFile(f);
    if (!displayName) setDisplayName(f.name.replace(/\.[^.]+$/, ""));
  };

  const submit = async () => {
    if (!file) { toast.error("Choose a file first."); return; }
    if (!catRoot || !category || !subcategory) {
      toast.error("Pick a category and subcategory."); return;
    }
    setBusy(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("entity_type", entityType);
      if (entityId) fd.append("entity_id", entityId);
      if (entityLabel) fd.append("entity_label", entityLabel);
      fd.append("category_root", catRoot);
      fd.append("category", category);
      fd.append("subcategory", subcategory);
      if (displayName) fd.append("display_name", displayName);
      if (documentTitle) fd.append("document_title", documentTitle);
      if (expiration) fd.append("expiration_date", expiration);
      if (notes) fd.append("notes", notes);
      if (tags) fd.append("tags", tags);
      await v2Upload(fd, (e) => {
        if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
      });
      toast.success("Document uploaded.");
      onUploaded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="documents-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2" style={{ color: "#0B2545" }}>
            <Upload className="h-5 w-5" /> Upload document
          </DialogTitle>
          <DialogDescription>
            Attach to any entity in the platform. Phase 2 will auto-fill metadata via AI OCR.
          </DialogDescription>
        </DialogHeader>

        {/* File picker */}
        <div onDragOver={(e) => e.preventDefault()}
             onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}
             className="mt-3 rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer"
             style={{ borderColor: file ? "#0F6D5D" : "#E2E8F0", background: file ? "rgba(15,109,93,0.04)" : "#F8FAFC" }}
             onClick={() => inputRef.current?.click()}
             data-testid="documents-upload-drop">
          <Upload className="h-6 w-6 mx-auto mb-2" style={{ color: file ? "#0F6D5D" : "#94A3B8" }} />
          <div className="text-sm font-medium" style={{ color: "#0B2545" }}>
            {file ? file.name : "Drag & drop, or click to browse"}
          </div>
          <div className="text-xs mt-1" style={{ color: "#64748B" }}>
            PDF · JPG · PNG · DOCX · XLSX · HEIC · up to 25 MB
          </div>
          <input ref={inputRef} type="file" className="hidden"
                 accept=".pdf,.jpg,.jpeg,.png,.docx,.doc,.xlsx,.xls,.webp,.heic,.heif"
                 onChange={(e) => pick(e.target.files?.[0])} />
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Attach to">
            <Select value={entityType} onValueChange={(v) => { setEntityType(v); setCatRoot(_entityForRoot(_rootForEntity(v))); }}>
              <SelectTrigger data-testid="upload-entity-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {entityTypes.map((e) => (
                  <SelectItem key={e} value={e}>{_prettify(e)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Category">
            <Select value={catRoot} onValueChange={(v) => { setCatRoot(v); setCategory(""); setSubcategory(""); }}>
              <SelectTrigger data-testid="upload-cat-root"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(V2_ROOT_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Sub-category">
            <Select value={category} onValueChange={(v) => { setCategory(v); setSubcategory(""); }}>
              <SelectTrigger data-testid="upload-category"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>
                {Object.keys(cats).map((k) => (
                  <SelectItem key={k} value={k}>{_prettify(k)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Document type">
            <Select value={subcategory} onValueChange={setSubcategory}>
              <SelectTrigger data-testid="upload-subcategory"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>
                {subs.map((s) => (
                  <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Entity ID (optional)">
            <Input value={entityId} onChange={(e) => setEntityId(e.target.value)}
                   placeholder="UUID of employee/facility/…"
                   data-testid="upload-entity-id" />
          </Field>
          <Field label="Entity label (optional)">
            <Input value={entityLabel} onChange={(e) => setEntityLabel(e.target.value)}
                   placeholder="Displayed if entity_id is unknown" />
          </Field>
          <Field label="Display name">
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                   data-testid="upload-display-name" />
          </Field>
          <Field label="Expiration date (optional)">
            <Input type="date" value={expiration} onChange={(e) => setExpiration(e.target.value)}
                   data-testid="upload-expiration" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Tags (comma-separated)">
              <Input value={tags} onChange={(e) => setTags(e.target.value)}
                     placeholder="urgent, renewed, cpr" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Notes">
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
          </div>
        </div>

        {busy && progress > 0 && (
          <div className="mt-3">
            <div className="h-2 rounded-full overflow-hidden" style={{ background: "#F1F5F9" }}>
              <div className="h-full transition-all" style={{ width: `${progress}%`, background: "#0B2545" }} />
            </div>
            <div className="text-[11px] mt-1" style={{ color: "#64748B" }}>Uploading… {progress}%</div>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !file || !category || !subcategory}
                  className="h-10" style={{ background: "#0B2545", color: "#FFD774" }}
                  data-testid="documents-upload-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------- helpers -------------------------------------------------------
const EmptyState = ({ onUpload }) => (
  <div className="rounded-2xl border-2 border-dashed py-16 text-center"
       style={{ borderColor: "#E2E8F0", color: "#64748B" }}
       data-testid="documents-empty">
    <Files className="h-8 w-8 mx-auto mb-3" style={{ color: "#94A3B8" }} />
    <div className="font-medium" style={{ color: "#0B2545" }}>No documents match the current filters.</div>
    <div className="text-sm mt-1 max-w-md mx-auto">Broaden your filters or upload something new.</div>
    <Button onClick={onUpload} className="mt-4"
            style={{ background: "#0B2545", color: "#FFD774" }}
            data-testid="documents-empty-upload">
      <Upload className="h-4 w-4 mr-2" /> Upload document
    </Button>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-[0.18em] font-medium mb-1" style={{ color: "#0B2545" }}>{label}</div>
    {children}
  </div>
);

const _fmtDate = (v) => { if (!v) return "—"; try { return format(parseISO(v), "PP"); } catch { return v; } };
const _fmtDT   = (v) => { if (!v) return "—"; try { return format(parseISO(v), "PPp"); } catch { return v; } };
const _fmtBytes = (n) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"]; let i = 0, v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
};
const _prettify = (k) => (k || "").replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const _categoryLabel = (doc) => {
  const parts = [doc.category_root, doc.category, doc.subcategory].filter(Boolean);
  return parts.map(_prettify).join(" · ");
};
const _rootForEntity = (e) => ({
  business: "business", employee: "employee", facility: "facility",
  contract_patient: "client", consumer: "client", lead: "client",
  contract: "business", financial: "financial", vendor: "business",
}[e] || "business");
const _entityForRoot = (r) => ({
  business: "business", employee: "employee", facility: "facility",
  client: "contract_patient", financial: "financial",
}[r] || "business");

const _expirationPill = (iso) => {
  if (!iso) return null;
  try {
    const days = differenceInCalendarDays(parseISO(iso), new Date());
    if (days < 0) return { label: `Expired ${-days}d`, bg: "rgba(155,28,28,0.10)", fg: "#9B1C1C" };
    if (days <= 30) return { label: `Exp ${days}d`, bg: "rgba(180,83,9,0.10)", fg: "#B45309" };
    if (days <= 60) return { label: `Exp ${days}d`, bg: "rgba(212,175,55,0.14)", fg: "#7A5E12" };
    if (days <= 90) return { label: `Exp ${days}d`, bg: "rgba(11,37,69,0.06)", fg: "#0B2545" };
    return null;
  } catch { return null; }
};
